import React, { useMemo, useEffect } from 'react';
import { type PreloadedQuery, graphql, usePreloadedQuery, useFragment } from 'react-relay';
import type {
	ThemeSetting,
	ColorThemeName,
	GradientThemeName,
	ImageThemeSetting,
} from '@jira/platform__project-theme-tokens/src/types.tsx';
import { NO_BACKGROUND_CONFIG } from '@jira/platform__project-theme-tokens/src/constants.tsx';
import {
	JIRA_PROJECT_TYPE_SOFTWARE_PROJECT,
	JIRA_PROJECT_TYPE_CORE_PROJECT,
} from '@jira/platform__project-types/src/project-types.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { ThemeTokensSetter } from '@jira/platform__project-theme-components/src/ui/theme-tokens-setter/ThemeTokensSetter.tsx';
import { DominantColorPersister } from './DominantColorPersister.tsx';
import type { ThemeSetterBackground$key } from './__generated__/ThemeSetterBackground.graphql';
import type { ThemeSetterQuery } from './__generated__/ThemeSetterQuery.graphql';

export const DEFAULT_THEME_SETTING: ThemeSetting = {
	type: 'color',
	value: NO_BACKGROUND_CONFIG.name,
};

export const ThemeSetter = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<ThemeSetterQuery>;
}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const data = usePreloadedQuery<ThemeSetterQuery>(
		graphql`
			query ThemeSetterQuery($cloudId: ID!, $projectIdOrKey: String!) @preloadable {
				jira @required(action: THROW) {
					mediaClientId(cloudId: $cloudId)
					mediaExternalEndpointUrl(cloudId: $cloudId)
				}
				jira_projectByIdOrKey(cloudId: $cloudId, idOrKey: $projectIdOrKey) {
					projectId
					projectType
					background {
						...ThemeSetterBackground
					}
					...DominantColorPersister_project @alias
					canEditProjectConfig: action(type: EDIT_PROJECT_CONFIG) {
						canPerform
					}
				}
			}
		`,
		queryReference,
	);

	const backgroundData = useFragment<ThemeSetterBackground$key>(
		graphql`
			fragment ThemeSetterBackground on JiraActiveBackgroundDetailsResult {
				__typename
				... on JiraColorBackground {
					colorValue
				}
				... on JiraGradientBackground {
					gradientValue
				}
				... on JiraMediaBackground {
					customBackground {
						id
						mediaApiFileId
						mediaReadToken(durationInSeconds: 900)
						sourceType
						sourceIdentifier
						altText
						dominantColor
						brightness
					}
				}
			}
		`,
		data.jira_projectByIdOrKey?.background ?? null,
	);

	const projectId = data.jira_projectByIdOrKey?.projectId;
	const isProjectAdmin = data.jira_projectByIdOrKey?.canEditProjectConfig?.canPerform;
	const dominantColorPersister = data.jira_projectByIdOrKey?.DominantColorPersister_project;

	const media = useMemo(() => {
		return data.jira.mediaClientId && data.jira.mediaExternalEndpointUrl
			? {
					clientId: data.jira.mediaClientId,
					externalEndpointUrl: data.jira.mediaExternalEndpointUrl,
				}
			: null;
	}, [data.jira.mediaClientId, data.jira.mediaExternalEndpointUrl]);

	const projectTheme = useMemo(() => {
		let themeSetting: ThemeSetting | null = null;

		if (!backgroundData) return null;

		switch (backgroundData.__typename) {
			case 'JiraColorBackground':
				themeSetting = {
					type: 'color',
					// oxlint-disable-next-line typescript/consistent-type-assertions
					value: backgroundData.colorValue as ColorThemeName,
				};
				break;
			case 'JiraGradientBackground':
				themeSetting = {
					type: 'color',
					// oxlint-disable-next-line typescript/consistent-type-assertions
					value: backgroundData.gradientValue as GradientThemeName,
				};
				break;
			case 'JiraMediaBackground':
				if (backgroundData.customBackground != null) {
					const {
						id,
						mediaApiFileId,
						mediaReadToken,
						sourceType,
						sourceIdentifier,
						altText,
						dominantColor,
						brightness,
					} = backgroundData.customBackground;
					// oxlint-disable-next-line typescript/consistent-type-assertions
					themeSetting = {
						type: 'image' as const,
						value: 'image' as const,
						id,
						mediaApiFileId,
						mediaReadToken,
						sourceType,
						sourceIdentifier,
						altText,
						dominantColor,
						brightness,
					} as ImageThemeSetting;
				}
				break;
			default:
				themeSetting = null;
		}

		return themeSetting
			? {
					themeSetting,
					entityId: `project/${projectId}`,
				}
			: null;
	}, [backgroundData, projectId]);

	const sourceType =
		projectTheme?.themeSetting?.type === 'image' ? projectTheme.themeSetting.sourceType : undefined;

	useEffect(() => {
		if (projectId) {
			if (
				data.jira_projectByIdOrKey?.projectType === JIRA_PROJECT_TYPE_CORE_PROJECT ||
				data.jira_projectByIdOrKey?.projectType === JIRA_PROJECT_TYPE_SOFTWARE_PROJECT
			) {
				const theme =
					sourceType === 'UNSPLASH' ? 'unsplash-image' : projectTheme?.themeSetting?.value;

				fireTrackAnalytics(createAnalyticsEvent({}), 'theme evaluated', {
					theme,
					projectId,
				});
			}
		}
	}, [
		projectTheme?.themeSetting?.value,
		sourceType,
		data.jira_projectByIdOrKey?.projectType,
		createAnalyticsEvent,
		projectId,
	]);

	return (
		<>
			{projectId &&
				isProjectAdmin &&
				projectTheme?.themeSetting?.type === 'image' &&
				!projectTheme.themeSetting.dominantColor &&
				media &&
				dominantColorPersister && (
					<DominantColorPersister
						themeSetting={projectTheme.themeSetting}
						media={media}
						projectFragment={dominantColorPersister}
					/>
				)}
			<ThemeTokensSetter media={media} themeSetting={projectTheme?.themeSetting || null} />
		</>
	);
};
